import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Flex,
  Icon,
} from '@kognitos/lattice';

export interface IReuseTestConnectionAlertProps {
  onReuse: () => void;
  disabled?: boolean;
  className?: string;
}

export function ReuseTestConnectionAlert({
  onReuse,
  disabled = false,
  className,
}: IReuseTestConnectionAlertProps) {
  const { t: tIntegrations } = useTranslation('integrations');
  const alertRef = useRef<HTMLDivElement>(null);

  // When the alert appears it can sit below the fold on integrations with many
  // credential/config fields. Scroll it into view on mount so the user always
  // sees why the Connect/Save button is disabled.
  useEffect(() => {
    alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <Alert
      ref={alertRef}
      variant="destructive"
      data-testid="reuse-test-connection-alert"
      className={`flex items-start gap-3 ${className ?? ''}`}
    >
      <Icon type="TriangleAlert" className="shrink-0" />
      <Flex direction="column" gap={1} className="flex-1">
        <AlertTitle>
          {tIntegrations(
            'integrations.addConnection.reuseTestConnection.title',
          )}
        </AlertTitle>
        <AlertDescription>
          {tIntegrations(
            'integrations.addConnection.reuseTestConnection.description',
          )}
        </AlertDescription>
      </Flex>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onReuse}
        disabled={disabled}
        className="self-center"
        data-testid="reuse-test-connection-action"
      >
        {tIntegrations('integrations.addConnection.reuseTestConnection.action')}
      </Button>
    </Alert>
  );
}
